import type { CSSProperties } from "react";
import { DisclosureChevron } from "@/components/common/Disclosure";
import {
	COUNT_BADGE_STYLE,
	DANGER_BADGE_STYLE,
	SECTION_HEADER_LABEL_STYLE,
	SECTION_HEADER_MIN_HEIGHT,
} from "@/lib/pane-styles";

/** 見出しの体裁は全セクションで共通。chevron が無い側も同じ幅を空けて字下げを合わせる。 */
const ROW_CLASS = "flex w-full items-center gap-1.5 border-0 bg-transparent text-left";
const ROW_STYLE: CSSProperties = { padding: "4px 12px", minHeight: SECTION_HEADER_MIN_HEIGHT };
// 字体だけを共通ヘッダから借りる。display:flex は持ち込まない — 省略記号は block container に
// しか効かず、flex を被せると 64 文字の phase 名が … 無しで字の途中から切れる。
// uppercase も外す (日本語では no-op だが Latin の phase 名を勝手に大文字化する)。
const LABEL_STYLE: CSSProperties = {
	...SECTION_HEADER_LABEL_STYLE,
	fontSize: "var(--text-caption)",
	fontWeight: 500,
	letterSpacing: "var(--tracking-label)",
	color: "var(--fg-3)",
};
// 畳んだ見出しに出るのは completed/total だけで、異常終了も「完了」として数に入る。全滅した
// フェーズが全成功と同じ「3/3」に見えるため、失敗を含む見出しはカードの失敗バッジと同じ配色にする。
const COUNT_FAILURE_STYLE: CSSProperties = { ...COUNT_BADGE_STYLE, ...DANGER_BADGE_STYLE };

/** 折りたたまないセクションの見出し。 */
export function SubagentSectionHeader({ label, count }: { label: string; count: string | number }) {
	return (
		<div className={ROW_CLASS} style={ROW_STYLE}>
			<span className="w-[11px] shrink-0" />
			<SectionLabel label={label} count={count} />
		</div>
	);
}

/** 折りたたみ付きセクションの見出し。 */
export function SubagentSectionToggle({
	label,
	count,
	collapsed,
	onToggle,
	testId,
	countFailure,
}: {
	label: string;
	count: string | number;
	collapsed: boolean;
	onToggle: () => void;
	testId: string;
	/** 配下に異常終了した run を含むか。畳んだままでも異常が分かるようにカウントを danger にする。 */
	countFailure?: boolean;
}) {
	return (
		<button
			type="button"
			data-testid={testId}
			data-collapsed={collapsed ? "true" : "false"}
			onClick={onToggle}
			className={`${ROW_CLASS} cursor-pointer`}
			style={ROW_STYLE}
		>
			<DisclosureChevron open={!collapsed} size={11} />
			<SectionLabel label={label} count={count} countFailure={countFailure} />
		</button>
	);
}

function SectionLabel({
	label,
	count,
	countFailure,
}: {
	label: string;
	count: string | number;
	countFailure?: boolean;
}) {
	return (
		<>
			<span style={LABEL_STYLE} title={label} data-tooltip-if-truncated>
				{label}
			</span>
			<span
				data-testid="subagent-section-count"
				data-count-failure={countFailure ? "true" : "false"}
				style={countFailure ? COUNT_FAILURE_STYLE : COUNT_BADGE_STYLE}
			>
				{count}
			</span>
		</>
	);
}
